import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Store, Plus, Edit2, Trash2, Save, X, MapPin, Phone, Mail, Globe, Palette, Eye, EyeOff, User, Shield, Crown } from 'lucide-react';

const RestaurantManagementUI = () => {
    const { user, restaurants: userRestaurants, hasPermission, canAccessRestaurant } = useAuth();
    
    const [restaurants, setRestaurants] = useState([]);
    const [editingRestaurant, setEditingRestaurant] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        logo: '',
        backgroundImage: '',
        defaultImage: '',
        primaryColor: '#FF6B35',
        secondaryColor: '#2C3E50',
        isActive: true,
        logoFile: null,
        logoPreview: '',
        backgroundFile: null,
        backgroundPreview: '',
        defaultImageFile: null,
        defaultImagePreview: ''
    });

    // Check if user is SuperAdmin
    const isSuperAdmin = () => {
        if (!user || !user.role) return false;
        const userRole = typeof user.role === 'number' ? 
            (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
            user.role;
        return userRole === 'SuperAdmin';
    };

    // Use restaurants from AuthContext instead of fetching separately
    useEffect(() => {
        if (user) {
            // Her zaman API'yi çağır
            fetchRestaurants();
        }
    }, [user]);

    // Cleanup blob URLs when component unmounts or formData changes
    useEffect(() => {
        return () => {
            if (formData.logoPreview && formData.logoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(formData.logoPreview);
            }
            if (formData.backgroundPreview && formData.backgroundPreview.startsWith('blob:')) {
                URL.revokeObjectURL(formData.backgroundPreview);
            }
            if (formData.defaultImagePreview && formData.defaultImagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(formData.defaultImagePreview);
            }
        };
    }, [formData.logoPreview, formData.backgroundPreview, formData.defaultImagePreview]);

    const fetchRestaurants = async () => {
        if (!user) {
            return;
        }
        
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            
            // Token kontrolü
            if (!token) {
                setError('Token bulunamadı. Lütfen tekrar giriş yapın.');
                setLoading(false);
                return;
            }
            
            let url = `${API_BASE_URL}/api/restaurants`;
            
            // Convert numeric role to string for comparison
            const userRole = typeof user.role === 'number' ? 
              (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
              user.role;
            
            // If user is not SuperAdmin, filter by their restaurant
            if (userRole !== 'SuperAdmin') {
                url = `${API_BASE_URL}/api/restaurants/user/${user.id}`;
            }

            console.log('Fetching restaurants with token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
            console.log('Request URL:', url);
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);
            
            if (response.ok) {
                const data = await response.json();
                setRestaurants(data);
            } else if (response.status === 401) {
                setError('Yetkilendirme hatası. Token geçersiz veya süresi dolmuş. Lütfen tekrar giriş yapın.');
                // Token'ı temizle
                localStorage.removeItem('authToken');
            } else {
                setError('Restoranlar yüklenirken hata oluştu');
            }
        } catch (err) {
            setError('API bağlantı hatası');
            console.error('API Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            address: '',
            phone: '',
            email: '',
            website: '',
            logo: '',
            backgroundImage: '',
            defaultImage: '',
            primaryColor: '#FF6B35',
            secondaryColor: '#2C3E50',
            isActive: true,
            logoFile: null,
            logoPreview: '',
            backgroundFile: null,
            backgroundPreview: '',
            defaultImageFile: null,
            defaultImagePreview: ''
        });
    };

    const handleCreate = () => {
        setIsCreating(true);
        resetForm();
    };

    const handleEdit = (restaurant) => {
        // Try different possible ID field names from API - prioritize 'id' field
        const possibleIds = [
            restaurant.id,  // Primary - API'den gelen doğru alan
            restaurant.restaurantId,
            restaurant.RestaurantId,
            restaurant.ID
        ];

        // Find the first non-null ID
        const actualId = possibleIds.find(id => id != null);

        if (!actualId) {
            setError('Restaurant ID bulunamadı');
            return;
        }

        setError('');
        setIsCreating(false);
        setEditingRestaurant(actualId);

        // Farklı field isimlerini kontrol et (camelCase ve PascalCase)
        const defaultImageValue = restaurant.defaultImage 
            || restaurant.DefaultImage 
            || restaurant.defaultImagePath 
            || restaurant.DefaultImagePath 
            || '';
        
        setFormData({
            name: restaurant.name || '',
            description: restaurant.description || '',
            address: restaurant.address || '',
            phone: restaurant.phone || '',
            email: restaurant.email || '',
            website: restaurant.website || '',
            logo: restaurant.logo || '',
            backgroundImage: restaurant.backgroundImage || '',
            defaultImage: defaultImageValue,
            primaryColor: restaurant.primaryColor || '#FF6B35',
            secondaryColor: restaurant.secondaryColor || '#2C3E50',
            isActive: restaurant.isActive !== undefined ? restaurant.isActive : true,
            logoFile: null,
            logoPreview: restaurant.logo ? `${API_BASE_URL}${restaurant.logo}` : '',
            backgroundFile: null,
            backgroundPreview: restaurant.backgroundImage ? `${API_BASE_URL}${restaurant.backgroundImage}` : '',
            defaultImageFile: null,
            defaultImagePreview: defaultImageValue ? `${API_BASE_URL}${defaultImageValue}` : ''
        });
    };

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const isValidUrl = (url) => {
        try {
            new URL(url.startsWith('http') ? url : `http://${url}`);
            return true;
        } catch {
            return false;
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');

        try {
            // Check if user can create/edit restaurants
            if (!hasPermission('manage_own_restaurant')) {
                setError('Restoran oluşturma/düzenleme yetkiniz bulunmamaktadır');
                return;
            }

            // Validation
            if (!formData.name.trim()) {
                setError('Restoran adı zorunludur');
                return;
            }

            // Diğer alanlar opsiyonel, sadece doldurulmuşsa validasyon yap
            if (formData.email && !isValidEmail(formData.email)) {
                setError('Geçerli bir email adresi girin');
                return;
            }

            if (formData.website && !isValidUrl(formData.website)) {
                setError('Geçerli bir website adresi girin');
                return;
            }

            if (formData.email && !isValidEmail(formData.email)) {
                setError('Geçerli bir email adresi girin');
                return;
            }

            if (formData.website && !isValidUrl(formData.website)) {
                setError('Geçerli bir website adresi girin');
                return;
            }

            const token = localStorage.getItem('authToken');
            
            // Edit modunda mevcut restaurant'tan değerleri al (boş alanlar için)
            let currentRestaurant = null;
            if (editingRestaurant) {
                currentRestaurant = restaurants.find(r => {
                    const possibleIds = [r.id, r.restaurantId, r.RestaurantId, r.ID];
                    return possibleIds.includes(editingRestaurant);
                });
            }
            
            // DTO'ya göre: Name, Description, Phone, Email, PrimaryColor, SecondaryColor zorunlu
            // Address, Website, Logo, BackgroundImage, DefaultImage opsiyonel (nullable)
            
            // Zorunlu alanları kontrol et ve mevcut restaurant'tan al
            // formData'dan al, yoksa currentRestaurant'tan al, o da yoksa varsayılan değer
            const name = (formData.name ? formData.name.trim() : '') || (currentRestaurant?.name || currentRestaurant?.Name) || '';
            const description = (formData.description ? formData.description.trim() : '') || (currentRestaurant?.description || currentRestaurant?.Description) || '';
            const phone = (formData.phone ? formData.phone.trim() : '') || (currentRestaurant?.phone || currentRestaurant?.Phone) || '';
            const email = (formData.email ? formData.email.trim() : '') || (currentRestaurant?.email || currentRestaurant?.Email) || '';
            const primaryColor = formData.primaryColor || (currentRestaurant?.primaryColor || currentRestaurant?.PrimaryColor) || '#FF6B35';
            const secondaryColor = formData.secondaryColor || (currentRestaurant?.secondaryColor || currentRestaurant?.SecondaryColor) || '#2C3E50';
            
            console.log('Form data değerleri:', {
                'formData.name': formData.name,
                'formData.description': formData.description,
                'formData.phone': formData.phone,
                'formData.email': formData.email,
                'currentRestaurant': currentRestaurant,
                'editingRestaurant': editingRestaurant
            });
            
            console.log('Hesaplanan değerler:', {
                name,
                description,
                phone,
                email,
                primaryColor,
                secondaryColor
            });
            
            // Zorunlu alan validasyonu
            if (!name) {
                setError('Restoran adı zorunludur');
                setLoading(false);
                return;
            }
            if (!description) {
                setError('Açıklama zorunludur');
                setLoading(false);
                return;
            }
            if (!phone) {
                setError('Telefon zorunludur');
                setLoading(false);
                return;
            }
            if (!email) {
                setError('Email zorunludur');
                setLoading(false);
                return;
            }
            
            // Backend DTO'ya göre FormData kullan (ProductsManagement ve CategoriesManagement gibi)
            const formDataToSend = new FormData();
            
            // Edit modunda Id ekle
            if (editingRestaurant) {
                formDataToSend.append('Id', editingRestaurant);
            }
            
            // Zorunlu alanlar
            formDataToSend.append('Name', name);
            formDataToSend.append('Description', description);
            formDataToSend.append('Phone', phone);
            formDataToSend.append('Email', email);
            formDataToSend.append('PrimaryColor', primaryColor);
            formDataToSend.append('SecondaryColor', secondaryColor);
            formDataToSend.append('IsActive', formData.isActive.toString());
            
            // Opsiyonel alanlar (nullable)
            const address = formData.address.trim() || (currentRestaurant?.address || currentRestaurant?.Address || '');
            const website = formData.website.trim() || (currentRestaurant?.website || currentRestaurant?.Website || '');
            const logo = formData.logo.trim() || (currentRestaurant?.logo || currentRestaurant?.Logo || '');
            const backgroundImage = formData.backgroundImage.trim() || (currentRestaurant?.backgroundImage || currentRestaurant?.BackgroundImage || '');
            
            // DefaultImage: Dosya seçildiyse formData'dan al, yoksa mevcut restaurant'tan al
            let defaultImage = '';
            if (formData.defaultImageFile) {
                // Yeni dosya seçildi, formData'dan path'i al
                defaultImage = formData.defaultImage.trim() || '';
            } else {
                // Dosya seçilmedi, mevcut restaurant'tan al
                defaultImage = currentRestaurant?.defaultImage || currentRestaurant?.DefaultImage || currentRestaurant?.defaultImagePath || currentRestaurant?.DefaultImagePath || '';
            }
            
            if (address) formDataToSend.append('Address', address);
            if (website) formDataToSend.append('Website', website);
            if (logo) formDataToSend.append('Logo', logo);
            if (backgroundImage) formDataToSend.append('BackgroundImage', backgroundImage);
            if (defaultImage) formDataToSend.append('DefaultImage', defaultImage);
            
            // Dosya yükleme için
            if (formData.logoFile) {
                formDataToSend.append('ImageFileLogo', formData.logoFile);
                console.log('Logo dosyası eklendi:', formData.logoFile.name);
            }
            if (formData.backgroundFile) {
                formDataToSend.append('ImageFileBackground', formData.backgroundFile);
                console.log('Background dosyası eklendi:', formData.backgroundFile.name);
            }
            if (formData.defaultImageFile) {
                formDataToSend.append('ImageFile', formData.defaultImageFile);
                console.log('DefaultImage dosyası eklendi:', formData.defaultImageFile.name, 'Path:', defaultImage);
            }
            
            console.log('FormData içeriği:');
            for (let [key, value] of formDataToSend.entries()) {
                if (value instanceof File) {
                    console.log(`${key}: File - ${value.name}`);
                } else {
                    console.log(`${key}: ${value}`);
                }
            }
            
            let response;
            let url;
            
            if (editingRestaurant) {
                url = `${API_BASE_URL}/api/restaurants/${editingRestaurant}`;
    
                response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`
                        // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
                    },
                    body: formDataToSend
                });
            } else {
                // Create modunda
                url = `${API_BASE_URL}/api/restaurants`;
                
                response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                        // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
                    },
                    body: formDataToSend
                });
            }
            


            if (response.ok) {
                // Refresh the list
                await fetchRestaurants();
                setIsCreating(false);
                setEditingRestaurant(null);
                resetForm();
            } else if (response.status === 500) {
                // Check if it's the EF Core trigger issue
                const errorText = await response.text();
                if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
                    // Wait a bit and refresh to see if data was actually saved despite the error
                    setTimeout(async () => {
                        await fetchRestaurants();

                        // Check if the restaurant was actually saved
                        const savedRestaurant = restaurants.find(r => r.name === formData.name);
                        if (savedRestaurant) {
                            setIsCreating(false);
                            setEditingRestaurant(null);
                            resetForm();
                            setError(''); // Clear error since data was actually saved
                        } else {
                            setError(`EF Core Hatası: ${errorText}`);
                        }
                    }, 1000);
                } else {
                    setError(`Server Hatası (500): ${errorText}`);
                }
            } else {
                const errorText = await response.text();
                console.log('Error Response Text:', errorText);
                setError(`API Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
            }
        } catch (err) {
            setError(`Bağlantı hatası: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsCreating(false);
        setEditingRestaurant(null);
        resetForm();
        setError('');
    };

    const handleDelete = async (restaurantId) => {
        if (window.confirm('Bu restoranı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
            try {
                // Get the restaurant to be deleted for verification
                const restaurantToDelete = restaurants.find(r => {
                    const possibleIds = [r.id, r.restaurantId, r.RestaurantId, r.ID];
                    return possibleIds.includes(restaurantId);
                });

                if (!restaurantToDelete) {
                    setError('Restaurant bulunamadı');
                    return;
                }

                const actualId = [restaurantToDelete.id, restaurantToDelete.restaurantId, restaurantToDelete.RestaurantId, restaurantToDelete.ID]
                    .find(id => id != null);

                

                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/api/restaurants/${actualId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });



                if (response.ok) {

                    await fetchRestaurants();
                } else if (response.status === 500) {
                    // Check if it's the EF Core trigger issue
                    const errorText = await response.text();
                    console.log('Delete Error Response:', errorText);

                    if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
                        console.warn('EF Core trigger issue detected during delete, checking if data was actually deleted...');

                        // Wait a bit and refresh to see if data was actually deleted despite the error
                        setTimeout(async () => {
                            await fetchRestaurants();

                            // Check if the restaurant was actually deleted
                            const stillExists = restaurants.find(r => {
                                const possibleIds = [r.id, r.restaurantId, r.RestaurantId, r.ID];
                                return possibleIds.includes(actualId);
                            });

                            if (!stillExists) {
                
                                setError(''); // Clear error since data was actually deleted
                            } else {
                                setError(`EF Core Silme Hatası: ${errorText}`);
                            }
                        }, 1000);
                    } else {
                        setError(`Server Hatası (500): ${errorText}`);
                    }
                } else {
                    const errorText = await response.text();
                    console.error('Delete API Error Response:', errorText);
                    setError(`Silme Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
                }
            } catch (err) {
                console.error('Delete Network Error:', err);
                setError(`Bağlantı hatası: ${err.message}`);
            }
        }
    };

    const toggleRestaurantStatus = async (restaurant) => {
        try {
            // Get the actual ID using the same logic as handleEdit
            const possibleIds = [restaurant.id, restaurant.restaurantId, restaurant.RestaurantId, restaurant.ID];
            const actualId = possibleIds.find(id => id != null);

            if (!actualId) {
                setError('Restaurant ID bulunamadı');
                return;
            }

            const updatedData = {
                name: restaurant.name,
                description: restaurant.description || null,
                address: restaurant.address,
                phone: restaurant.phone || null,
                email: restaurant.email || null,
                website: restaurant.website || null,
                logo: restaurant.logo || null,
                backgroundImage: restaurant.backgroundImage || null,
                primaryColor: restaurant.primaryColor || '#FF6B35',
                secondaryColor: restaurant.secondaryColor || '#2C3E50',
                isActive: !restaurant.isActive
            };



            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/restaurants/${actualId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedData)
            });

            

            if (response.ok) {
                await fetchRestaurants();
            } else if (response.status === 500) {
                const errorText = await response.text();
                if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
                    console.warn('EF Core trigger issue detected during status toggle, checking if status was actually changed...');
                    setTimeout(async () => {
                        await fetchRestaurants();
                        setError(''); // Clear any error since data likely updated
                    }, 1000);
                } else {
                    setError(`Durum güncellenirken hata oluştu: ${errorText}`);
                }
            } else {
                const errorText = await response.text();
                setError(`Durum güncellenirken hata oluştu: ${errorText}`);
            }
        } catch (err) {
            setError('API bağlantı hatası');
            console.error('Toggle API Error:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getInitials = (name) => {
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Image preview functions
    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Önceki blob URL'i temizle
            setFormData(prev => {
                if (prev.logoPreview && prev.logoPreview.startsWith('blob:')) {
                    URL.revokeObjectURL(prev.logoPreview);
                }
                return prev;
            });
            
            const imageUrl = URL.createObjectURL(file);
            const fileName = file.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
            const imagePath = `/MenuResim/${fileName}`;
            
            setFormData(prev => ({
                ...prev,
                logoFile: file,
                logo: imagePath,
                logoPreview: imageUrl
            }));
        }
    };

    const handleBackgroundUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Önceki blob URL'i temizle
            setFormData(prev => {
                if (prev.backgroundPreview && prev.backgroundPreview.startsWith('blob:')) {
                    URL.revokeObjectURL(prev.backgroundPreview);
                }
                return prev;
            });
            
            const imageUrl = URL.createObjectURL(file);
            const fileName = file.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
            const imagePath = `/MenuResim/${fileName}`;
            
            setFormData(prev => ({
                ...prev,
                backgroundFile: file,
                backgroundImage: imagePath,
                backgroundPreview: imageUrl
            }));
        }
    };

    const handleDefaultImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Önceki blob URL'i temizle
            setFormData(prev => {
                if (prev.defaultImagePreview && prev.defaultImagePreview.startsWith('blob:')) {
                    URL.revokeObjectURL(prev.defaultImagePreview);
                }
                return prev;
            });
            
            const imageUrl = URL.createObjectURL(file);
            const fileName = file.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
            const imagePath = `/MenuResim/${fileName}`;
            
            setFormData(prev => ({
                ...prev,
                defaultImageFile: file,
                defaultImage: imagePath,
                defaultImagePreview: imageUrl
            }));
        }
    };

        return (
        <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg shadow-sm">
                {/* Header */}
                <div className="border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <Store className="h-6 w-6 text-orange-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Restoran Yönetimi</h1>
                                <p className="text-gray-600">
                                    {user.role === 'SuperAdmin' 
                                        ? 'Tüm restoranları yönetin' 
                                        : 'Kendi restoranınızı yönetin'
                                    }
                                </p>
                            </div>
                        </div>
                        
                        {/* User Info */}
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                    {user.role === 'SuperAdmin' ? <Crown className="w-4 h-4 text-purple-600" /> : 
                                     user.role === 'Admin' ? <Shield className="w-4 h-4 text-blue-600" /> : 
                                     <User className="w-4 h-4 text-green-600" />}
                                    {user.role}
                                </span>
                                <span>•</span>
                                <span>{restaurants.length} restoran</span>
                            </div>
                            
                            {!isCreating && !editingRestaurant && isSuperAdmin() && (
                                <button
                                    onClick={handleCreate}
                                    className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span>Yeni Restoran</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                

                {/* Form */}
                {(isCreating || editingRestaurant) && (
                    <div className="border-b border-gray-200 p-6 bg-gray-50">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">
                            {editingRestaurant ? 'Restoranı Düzenle' : 'Yeni Restoran Ekle'}
                        </h2>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-600 text-sm">{error}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h3 className="text-md font-medium text-gray-900">Temel Bilgiler</h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Restoran Adı *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Restoran adını girin"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Açıklama
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Restoran hakkında kısa açıklama"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Adres *
                                    </label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows="2"
                                        placeholder="Tam adres bilgisi"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>

                                <div className="flex items-center space-x-3">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                                        Restoran aktif
                                    </label>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="space-y-4">
                                <h3 className="text-md font-medium text-gray-900">İletişim Bilgileri</h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Telefon
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="+90 XXX XXX XX XX"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="info@restoran.com"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Website
                                    </label>
                                    <input
                                        type="url"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        placeholder="www.restoran.com"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Logo
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoUpload}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                    
                                    {/* Preview */}
                                    {formData.logoPreview && (
                                        <div className="mt-2">
                                            <p className="text-xs text-gray-600 mb-1">Önizleme:</p>
                                            <img
                                                key={formData.logoPreview}
                                                src={formData.logoPreview}
                                                alt="Logo önizleme"
                                                className="w-16 h-16 object-cover rounded-lg border border-gray-300"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Arkaplan Resmi
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleBackgroundUpload}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                    
                                    {/* Preview */}
                                    {formData.backgroundPreview && (
                                        <div className="mt-2">
                                            <p className="text-xs text-gray-600 mb-1">Önizleme:</p>
                                            <img
                                                key={formData.backgroundPreview}
                                                src={formData.backgroundPreview}
                                                alt="Arkaplan önizleme"
                                                className="w-full h-20 object-cover rounded-lg border border-gray-300"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Mevcut ürün resmi
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleDefaultImageUpload}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    />
                                    
                                    {/* Preview */}
                                    {formData.defaultImagePreview && (
                                        <div className="mt-2">
                                            <p className="text-xs text-gray-600 mb-1">Önizleme:</p>
                                            <img
                                                key={formData.defaultImagePreview}
                                                src={formData.defaultImagePreview}
                                                alt="Mevcut ürün resmi önizleme"
                                                className="w-full h-20 object-cover rounded-lg border border-gray-300"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Design Settings */}
                            <div className="md:col-span-2 border-t pt-4">
                                <h3 className="text-md font-medium text-gray-900 mb-4">Tasarım Ayarları</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Ana Renk
                                        </label>
                                        <div className="flex items-center space-x-3">
                                            <input
                                                type="color"
                                                name="primaryColor"
                                                value={formData.primaryColor}
                                                onChange={handleInputChange}
                                                className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                name="primaryColor"
                                                value={formData.primaryColor}
                                                onChange={handleInputChange}
                                                placeholder="#FF6B35"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            İkincil Renk
                                        </label>
                                        <div className="flex items-center space-x-3">
                                            <input
                                                type="color"
                                                name="secondaryColor"
                                                value={formData.secondaryColor}
                                                onChange={handleInputChange}
                                                className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                                            />
                                            <input
                                                type="text"
                                                name="secondaryColor"
                                                value={formData.secondaryColor}
                                                onChange={handleInputChange}
                                                placeholder="#2C3E50"
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Color Preview */}
                                <div className="mt-4 p-4 rounded-lg border border-gray-200">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Renk Önizlemesi:</p>
                                    <div className="flex items-center space-x-4">
                                        <div
                                            className="w-12 h-12 rounded-lg border border-gray-300"
                                            style={{ backgroundColor: formData.primaryColor }}
                                            title={`Ana Renk: ${formData.primaryColor}`}
                                        ></div>
                                        <div
                                            className="w-12 h-12 rounded-lg border border-gray-300"
                                            style={{ backgroundColor: formData.secondaryColor }}
                                            title={`İkincil Renk: ${formData.secondaryColor}`}
                                        ></div>
                                        <div className="text-sm text-gray-600">
                                            Ana ve ikincil renklerinizin menüde nasıl görüneceği
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3 mt-6">
                            <button
                                onClick={handleSave}
                                disabled={loading}
                                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                            >
                                <Save className="h-4 w-4" />
                                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
                            </button>
                            <button
                                onClick={handleCancel}
                                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                <X className="h-4 w-4" />
                                <span>İptal</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Restaurants List */}
                <div className="p-6">
                    {loading && restaurants.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                            <p className="text-gray-600 mt-4">Yükleniyor...</p>
                        </div>
                    ) : restaurants.length === 0 ? (
                        <div className="text-center py-12">
                            <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz restoran eklenmemiş</h3>
                            <p className="text-gray-600 mb-4">İlk restoranınızı ekleyerek başlayın</p>
                            {isSuperAdmin() && (
                                <button
                                    onClick={handleCreate}
                                    className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                                >
                                    Restoran Ekle
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {restaurants.map((restaurant) => (
                                <div key={restaurant.id || restaurant.restaurantId} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                                    {/* Header with background color */}
                                    <div
                                        className="h-24 relative"
                                        style={{
                                            backgroundColor: restaurant.primaryColor || '#FF6B35',
                                            backgroundImage: restaurant.backgroundImage ? `url(${restaurant.backgroundImage.startsWith('http') ? restaurant.backgroundImage : `${API_BASE_URL}${restaurant.backgroundImage}`})` : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center'
                                        }}
                                    >
                                        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                                        <div className="absolute top-3 right-3 flex items-center space-x-2">
                                            <button
                                                onClick={() => toggleRestaurantStatus(restaurant)}
                                                className={`p-1 rounded-full ${restaurant.isActive ? 'bg-green-500' : 'bg-gray-500'} text-white`}
                                            >
                                                {restaurant.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                            </button>
                                        </div>

                                        {/* Logo or Initials */}
                                        <div className="absolute bottom-0 left-4 transform translate-y-1/2">
                                            {restaurant.logo ? (
                                                <img
                                                    src={restaurant.logo.startsWith('http') ? restaurant.logo : `${API_BASE_URL}${restaurant.logo}`}
                                                    alt={restaurant.name}
                                                    className="w-16 h-16 rounded-full border-4 border-white object-cover bg-white"
                                                    onError={(e) => {
                                        
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center text-white font-bold text-lg"
                                                    style={{ backgroundColor: restaurant.secondaryColor || '#2C3E50' }}
                                                >
                                                    {getInitials(restaurant.name)}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="pt-10 p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-900 mb-1">{restaurant.name}</h3>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${restaurant.isActive
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {restaurant.isActive ? 'Aktif' : 'Pasif'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center space-x-1">
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleEdit(restaurant);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-orange-600 transition-colors"
                                                    title="Düzenle"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        // Use the same ID detection logic as handleEdit
                                                        const possibleIds = [restaurant.restaurantId, restaurant.id, restaurant.RestaurantId, restaurant.ID];
                                                        const actualId = possibleIds.find(id => id != null);
                                                        if (actualId) {
                                                            handleDelete(actualId);
                                                        } else {
                                                            setError('Restaurant ID bulunamadı');
                                                        }
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                                    title="Sil"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {restaurant.description && (
                                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{restaurant.description}</p>
                                        )}

                                        <div className="space-y-2 text-sm text-gray-500">
                                            {restaurant.address && (
                                                <div className="flex items-start space-x-2">
                                                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                                    <span className="line-clamp-2">{restaurant.address}</span>
                                                </div>
                                            )}

                                            {restaurant.phone && (
                                                <div className="flex items-center space-x-2">
                                                    <Phone className="h-4 w-4" />
                                                    <span>{restaurant.phone}</span>
                                                </div>
                                            )}

                                            {restaurant.email && (
                                                <div className="flex items-center space-x-2">
                                                    <Mail className="h-4 w-4" />
                                                    <span className="truncate">{restaurant.email}</span>
                                                </div>
                                            )}

                                            {restaurant.website && (
                                                <div className="flex items-center space-x-2">
                                                    <Globe className="h-4 w-4" />

                                                   <a href={restaurant.website.startsWith('http') ? restaurant.website : `https://${restaurant.website}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 truncate"
                                                   >
                                                    {restaurant.website}
                                                </a>
                                               </div>
                                           )}
                                    </div>

                                    {restaurant.createdDate && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <p className="text-xs text-gray-400">
                                                Oluşturulma: {formatDate(restaurant.createdDate)}
                                            </p>
                                        </div>
                                    )}

                                    {/* Color indicators */}
                                    <div className="mt-3 flex items-center space-x-2">
                                        <Palette className="h-4 w-4 text-gray-400" />
                                        <div className="flex space-x-1">
                                            <div
                                                className="w-4 h-4 rounded border border-gray-300"
                                                style={{ backgroundColor: restaurant.primaryColor }}
                                                title={`Ana Renk: ${restaurant.primaryColor}`}
                                            ></div>
                                            <div
                                                className="w-4 h-4 rounded border border-gray-300"
                                                style={{ backgroundColor: restaurant.secondaryColor }}
                                                title={`İkincil Renk: ${restaurant.secondaryColor}`}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                               </div>
                    ))}
                </div>
                   )}
            </div>
        </div>
        </div>
   );
};

export default RestaurantManagementUI;